import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Users, DollarSign, Rocket, TrendingUp, ArrowLeft, Download, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Admin = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total_users: 0,
    total_revenue: 0,
    total_transactions: 0,
    total_contracts: 0
  });
  const [users, setUsers] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [revenue, setRevenue] = useState([]);
  const [withdrawing, setWithdrawing] = useState(false);
  const [withdrawals, setWithdrawals] = useState([]);
  const [walletInfo, setWalletInfo] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [earningsSummary, setEarningsSummary] = useState(null);

  useEffect(() => {
    fetchAdminData();
  }, []);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  };

  const handleWithdrawEarnings = async () => {
    setWithdrawing(true);
    try {
      const response = await axios.post(`${API}/admin/withdraw-earnings`, {}, {
        headers: getAuthHeaders()
      });
      
      if (response.data.success) {
        toast.success(`Withdrew ${response.data.amount_eth.toFixed(4)} ETH to your wallet!`);
        toast.success(`≈ $${response.data.amount_usd.toFixed(2)} USD`, { duration: 5000 });
        toast.info(`TX: ${response.data.tx_hash.substring(0, 20)}...`, { duration: 10000 });
        if (response.data.etherscan_url) {
          toast.info(<a href={response.data.etherscan_url} target="_blank" rel="noopener noreferrer">View on Etherscan</a>, { duration: 15000 });
        }
        fetchAdminData();
      } else {
        toast.warning(response.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to withdraw earnings');
    } finally {
      setWithdrawing(false);
    }
  };

  const fetchAdminData = async () => {
    setLoading(true);
    try {
      const [statsRes, usersRes, txRes, contractsRes, revenueRes, withdrawalsRes, walletRes, analyticsRes, summaryRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers: getAuthHeaders() }),
        axios.get(`${API}/admin/users`, { headers: getAuthHeaders() }),
        axios.get(`${API}/admin/transactions`, { headers: getAuthHeaders() }),
        axios.get(`${API}/admin/contracts`, { headers: getAuthHeaders() }),
        axios.get(`${API}/admin/revenue`, { headers: getAuthHeaders() }),
        axios.get(`${API}/admin/withdrawal-history`, { headers: getAuthHeaders() }),
        axios.get(`${API}/admin/wallet-info`, { headers: getAuthHeaders() }),
        axios.get(`${API}/admin/earnings-analytics`, { headers: getAuthHeaders() }),
        axios.get(`${API}/admin/earnings-summary`, { headers: getAuthHeaders() })
      ]);

      setStats(statsRes.data);
      setUsers(usersRes.data.users);
      setTransactions(txRes.data.transactions);
      setContracts(contractsRes.data.contracts);
      setRevenue(revenueRes.data.revenue);
      setWithdrawals(withdrawalsRes.data.withdrawals);
      setWalletInfo(walletRes.data);
      setAnalytics(analyticsRes.data);
      setEarningsSummary(summaryRes.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Access denied. Admin only.');
        navigate('/dashboard');
      } else {
        toast.error('Failed to load admin data');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-white">Loading admin panel...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="admin-page">
      {/* Navigation */}
      <nav className="glass border-b border-border sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/dashboard')}
              data-testid="back-btn"
            >
              <ArrowLeft className="h-4 w-4 mr-2" /> Back
            </Button>
            <h1 className="text-2xl font-bold">Admin Back Office</h1>
          </div>
          <Button onClick={fetchAdminData} variant="outline">
            Refresh Data
          </Button>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
        {/* Earnings Summary Alert */}
        {earningsSummary && earningsSummary.should_withdraw && (
          <div className="mb-6 p-4 bg-[#10b981]/10 border border-[#10b981] rounded-lg flex items-center justify-between">
            <div className="flex items-center gap-3">
              <DollarSign className="h-8 w-8 text-[#10b981]" />
              <div>
                <p className="font-semibold text-lg">💰 {earningsSummary.message}</p>
                <p className="text-sm text-muted-foreground">Ready to transfer {earningsSummary.available_eth.toFixed(4)} ETH to your wallet</p>
              </div>
            </div>
            <Button
              onClick={handleWithdrawEarnings}
              disabled={withdrawing}
              className="bg-[#10b981] hover:bg-[#059669]"
            >
              Withdraw Now →
            </Button>
          </div>
        )}

        {/* Wallet & Earnings Section */}
        {walletInfo && (
          <Card className="glass border-border mb-8 border-[#10b981]" data-testid="wallet-info-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-[#10b981]" />
                Platform Earnings & Withdrawal
              </CardTitle>
              <CardDescription>Transfer accumulated fees to your personal MetaMask wallet</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-3 gap-6 mb-6">
                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground">Platform Master Wallet</p>
                  <p className="text-xs mono break-all bg-secondary p-2 rounded">
                    {walletInfo.master_wallet}
                  </p>
                  <p className="text-2xl font-bold text-[#10b981]">
                    {parseFloat(walletInfo.master_balance_eth).toFixed(4)} ETH
                  </p>
                  <p className="text-xs text-muted-foreground">Available to withdraw</p>
                </div>
                
                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground">Your Personal Wallet</p>
                  <p className="text-xs mono break-all bg-secondary p-2 rounded">
                    {walletInfo.admin_wallet}
                  </p>
                  <p className="text-2xl font-bold">
                    {parseFloat(walletInfo.admin_balance_eth).toFixed(4)} ETH
                  </p>
                  <p className="text-xs text-muted-foreground">Current balance</p>
                </div>

                <div className="flex items-center justify-center">
                  <Button
                    onClick={handleWithdrawEarnings}
                    disabled={withdrawing || parseFloat(walletInfo.master_balance_eth) < 0.01}
                    className="w-full bg-[#10b981] hover:bg-[#059669] h-16 text-lg"
                    data-testid="withdraw-earnings-btn"
                  >
                    {withdrawing ? (
                      <>
                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <Download className="mr-2 h-5 w-5" />
                        Withdraw Earnings
                      </>
                    )}
                  </Button>
                </div>
              </div>

              {walletInfo.error && (
                <div className="bg-yellow-500/10 border border-yellow-500/50 rounded p-3 text-sm text-yellow-500">
                  ⚠️ {walletInfo.error}
                </div>
              )}

              {withdrawals.length > 0 && (
                <div className="mt-6">
                  <h3 className="text-sm font-semibold mb-3">Recent Withdrawals</h3>
                  <div className="space-y-2">
                    {withdrawals.slice(0, 3).map((w, idx) => (
                      <div key={idx} className="flex justify-between items-center text-sm bg-secondary p-3 rounded">
                        <div>
                          <p className="font-semibold">{parseFloat(w.amount_eth).toFixed(4)} ETH</p>
                          <p className="text-xs text-muted-foreground">
                            {new Date(w.withdrawn_at).toLocaleString()}
                          </p>
                        </div>
                        <a 
                          href={`https://etherscan.io/tx/${w.tx_hash}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[#3b82f6] hover:underline text-xs"
                        >
                          View TX →
                        </a>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Stats Overview */}
        <div className="grid md:grid-cols-4 gap-6 mb-8">
          <Card className="glass border-border" data-testid="stat-users">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Total Users</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{stats.total_users}</div>
            </CardContent>
          </Card>

          <Card className="glass border-border" data-testid="stat-revenue">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Total Revenue</CardTitle>
              <DollarSign className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-[#10b981]">
                ${stats.total_revenue.toFixed(2)}
              </div>
            </CardContent>
          </Card>

          <Card className="glass border-border" data-testid="stat-transactions">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Transactions</CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{stats.total_transactions}</div>
            </CardContent>
          </Card>

          <Card className="glass border-border" data-testid="stat-contracts">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Contracts Deployed</CardTitle>
              <Rocket className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{stats.total_contracts}</div>
            </CardContent>
          </Card>
        </div>

        {/* Detailed Data */}
        <Tabs defaultValue="users" className="w-full">
          <TabsList className="glass w-full grid grid-cols-6 mb-6">
            <TabsTrigger value="users">Users</TabsTrigger>
            <TabsTrigger value="transactions">Transactions</TabsTrigger>
            <TabsTrigger value="contracts">Contracts</TabsTrigger>
            <TabsTrigger value="revenue">Revenue</TabsTrigger>
            <TabsTrigger value="withdrawals">Withdrawals</TabsTrigger>
            <TabsTrigger value="analytics">Analytics</TabsTrigger>
          </TabsList>

          {/* Users Tab */}
          <TabsContent value="users">
            <Card className="glass border-border">
              <CardHeader>
                <CardTitle>All Users</CardTitle>
                <CardDescription>Registered users on the platform</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-3 px-4">Name</th>
                        <th className="text-left py-3 px-4">Email</th>
                        <th className="text-left py-3 px-4">Wallet</th>
                        <th className="text-left py-3 px-4">Joined</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((user, idx) => (
                        <tr key={user.id || idx} className="border-b border-border/50">
                          <td className="py-3 px-4">{user.name}</td>
                          <td className="py-3 px-4">{user.email}</td>
                          <td className="py-3 px-4 mono text-xs">
                            {user.wallet_address ? `${user.wallet_address.substring(0, 10)}...` : 'None'}
                          </td>
                          <td className="py-3 px-4 text-sm text-muted-foreground">
                            {new Date(user.created_at).toLocaleDateString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {users.length === 0 && (
                    <div className="text-center py-8 text-muted-foreground">
                      No users yet
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Transactions Tab */}
          <TabsContent value="transactions">
            <Card className="glass border-border">
              <CardHeader>
                <CardTitle>All Transactions</CardTitle>
                <CardDescription>Payment transactions and purchases</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-3 px-4">User</th>
                        <th className="text-left py-3 px-4">Amount</th>
                        <th className="text-left py-3 px-4">Type</th>
                        <th className="text-left py-3 px-4">Status</th>
                        <th className="text-left py-3 px-4">Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transactions.map((tx, idx) => (
                        <tr key={tx.session_id || idx} className="border-b border-border/50">
                          <td className="py-3 px-4">{tx.user_email || tx.user_id}</td>
                          <td className="py-3 px-4 font-semibold">${tx.amount_usd}</td>
                          <td className="py-3 px-4 capitalize">{tx.transaction_type}</td>
                          <td className="py-3 px-4">
                            <span className={`px-2 py-1 rounded text-xs ${
                              tx.payment_status === 'paid' 
                                ? 'bg-[#10b981]/20 text-[#10b981]' 
                                : 'bg-yellow-500/20 text-yellow-500'
                            }`}>
                              {tx.payment_status}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-sm text-muted-foreground">
                            {new Date(tx.created_at).toLocaleString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {transactions.length === 0 && (
                    <div className="text-center py-8 text-muted-foreground">
                      No transactions yet
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Contracts Tab */}
          <TabsContent value="contracts">
            <Card className="glass border-border">
              <CardHeader>
                <CardTitle>Deployed Contracts</CardTitle>
                <CardDescription>All ERC20 tokens deployed through the platform</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-3 px-4">Token</th>
                        <th className="text-left py-3 px-4">Symbol</th>
                        <th className="text-left py-3 px-4">Supply</th>
                        <th className="text-left py-3 px-4">Contract Address</th>
                        <th className="text-left py-3 px-4">Deployed</th>
                      </tr>
                    </thead>
                    <tbody>
                      {contracts.map((contract, idx) => (
                        <tr key={contract.contract_address || idx} className="border-b border-border/50">
                          <td className="py-3 px-4">{contract.token_name}</td>
                          <td className="py-3 px-4 font-semibold">{contract.token_symbol}</td>
                          <td className="py-3 px-4">{contract.total_supply?.toLocaleString()}</td>
                          <td className="py-3 px-4 mono text-xs">
                            {contract.contract_address?.substring(0, 15)}...
                          </td>
                          <td className="py-3 px-4 text-sm text-muted-foreground">
                            {new Date(contract.deployed_at).toLocaleDateString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {contracts.length === 0 && (
                    <div className="text-center py-8 text-muted-foreground">
                      No contracts deployed yet
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Revenue Tab */}
          <TabsContent value="revenue">
            <Card className="glass border-border">
              <CardHeader>
                <CardTitle>Revenue Breakdown</CardTitle>
                <CardDescription>Platform earnings from fees</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex justify-between items-center p-4 bg-secondary rounded-lg">
                    <div>
                      <div className="text-sm text-muted-foreground">Transaction Fees</div>
                      <div className="text-2xl font-bold text-[#10b981]">
                        ${stats.transaction_fees?.toFixed(2) || '0.00'}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex justify-between items-center p-4 bg-secondary rounded-lg">
                    <div>
                      <div className="text-sm text-muted-foreground">Deployment Fees</div>
                      <div className="text-2xl font-bold text-[#10b981]">
                        ${stats.deployment_fees?.toFixed(2) || '0.00'}
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-between items-center p-4 bg-secondary rounded-lg">
                    <div>
                      <div className="text-sm text-muted-foreground">Subscription Revenue</div>
                      <div className="text-2xl font-bold text-[#10b981]">
                        ${stats.subscription_revenue?.toFixed(2) || '0.00'}
                      </div>
                    </div>
                  </div>

                  <div className="border-t border-border pt-4 mt-4">
                    <div className="flex justify-between items-center p-4 bg-[#10b981]/10 rounded-lg">
                      <div>
                        <div className="text-sm text-muted-foreground">Total Revenue</div>
                        <div className="text-3xl font-bold text-[#10b981]">
                          ${stats.total_revenue?.toFixed(2) || '0.00'}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Note */}
                  <div className="mt-6 text-sm text-muted-foreground text-center">
                    <p>Use the "Withdraw Earnings" button at the top to transfer fees to your wallet
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics">
            <Card className="glass border-border">
              <CardHeader>
                <CardTitle>Earnings Analytics</CardTitle>
                <CardDescription>Detailed breakdown of your platform earnings</CardDescription>
              </CardHeader>
              <CardContent>
                {analytics ? (
                  <div className="space-y-6">
                    {/* Summary Cards */}
                    <div className="grid md:grid-cols-3 gap-4">
                      <div className="p-4 bg-secondary rounded-lg">
                        <p className="text-sm text-muted-foreground mb-1">Total Fees Collected</p>
                        <p className="text-3xl font-bold text-[#10b981]">${analytics.total_fees_collected_usd?.toFixed(2)}</p>
                        <p className="text-xs text-muted-foreground mt-1">From {analytics.total_transactions} transactions</p>
                      </div>
                      
                      <div className="p-4 bg-secondary rounded-lg">
                        <p className="text-sm text-muted-foreground mb-1">Available to Withdraw</p>
                        <p className="text-3xl font-bold text-[#3b82f6]">${analytics.available_balance_usd?.toFixed(2)}</p>
                        <p className="text-xs text-muted-foreground mt-1">{analytics.available_balance_eth?.toFixed(4)} ETH</p>
                      </div>
                      
                      <div className="p-4 bg-secondary rounded-lg">
                        <p className="text-sm text-muted-foreground mb-1">Total Withdrawn</p>
                        <p className="text-3xl font-bold">${analytics.total_withdrawn_usd?.toFixed(2)}</p>
                        <p className="text-xs text-muted-foreground mt-1">{analytics.total_withdrawals} withdrawals</p>
                      </div>
                    </div>

                    {/* Time Period Analysis */}
                    <div>
                      <h3 className="text-lg font-semibold mb-4">Earnings by Time Period</h3>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="p-4 bg-[#10b981]/10 border border-[#10b981]/30 rounded-lg">
                          <p className="text-sm text-muted-foreground mb-1">Last 7 Days</p>
                          <p className="text-2xl font-bold text-[#10b981]">${analytics.weekly_fees_usd?.toFixed(2)}</p>
                          <p className="text-xs text-muted-foreground mt-1">Weekly earnings</p>
                        </div>
                        
                        <div className="p-4 bg-[#3b82f6]/10 border border-[#3b82f6]/30 rounded-lg">
                          <p className="text-sm text-muted-foreground mb-1">Last 30 Days</p>
                          <p className="text-2xl font-bold text-[#3b82f6]">${analytics.monthly_fees_usd?.toFixed(2)}</p>
                          <p className="text-xs text-muted-foreground mt-1">Monthly earnings</p>
                        </div>
                      </div>
                    </div>

                    {/* Key Metrics */}
                    <div>
                      <h3 className="text-lg font-semibold mb-4">Key Metrics</h3>
                      <div className="grid md:grid-cols-3 gap-4">
                        <div className="p-3 bg-secondary rounded">
                          <p className="text-xs text-muted-foreground">Avg Fee per Transaction</p>
                          <p className="text-xl font-bold mt-1">${analytics.average_transaction_fee?.toFixed(2)}</p>
                        </div>
                        
                        <div className="p-3 bg-secondary rounded">
                          <p className="text-xs text-muted-foreground">Fee Percentage</p>
                          <p className="text-xl font-bold mt-1">{analytics.fee_percentage}%</p>
                        </div>
                        
                        <div className="p-3 bg-secondary rounded">
                          <p className="text-xs text-muted-foreground">Current ETH Price</p>
                          <p className="text-xl font-bold mt-1">${analytics.eth_price?.toLocaleString()}</p>
                        </div>
                      </div>
                    </div>

                    {/* Withdrawal Recommendation */}
                    {analytics.available_balance_eth > 0.05 && (
                      <div className="p-4 bg-[#10b981]/10 border border-[#10b981] rounded-lg">
                        <p className="font-semibold text-[#10b981] mb-2">✅ Withdrawal Recommended</p>
                        <p className="text-sm text-muted-foreground">
                          You have ${analytics.available_balance_usd?.toFixed(2)} ready to withdraw. 
                          Transfer to your wallet to secure your earnings!
                        </p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-12 text-muted-foreground">
                    Loading analytics...
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Withdrawals Tab */}
          <TabsContent value="withdrawals">
            <Card className="glass border-border">
              <CardHeader>
                <CardTitle>Earnings Withdrawal History</CardTitle>
                <CardDescription>Track all transfers from master wallet to your personal wallet</CardDescription>
              </CardHeader>
              <CardContent>
                {withdrawals.length > 0 ? (
                  <div className="space-y-4">
                    {withdrawals.map((w, idx) => (
                      <div key={idx} className="flex justify-between items-center p-4 bg-secondary rounded-lg">
                        <div>
                          <div className="font-semibold text-lg">{parseFloat(w.amount_eth).toFixed(4)} ETH</div>
                          <div className="text-sm text-muted-foreground">
                            {new Date(w.withdrawn_at).toLocaleString()}
                          </div>
                          <div className="text-xs text-muted-foreground mono mt-1">
                            From: {w.from_wallet?.substring(0, 15)}...
                          </div>
                          <div className="text-xs text-muted-foreground mono">
                            To: {w.to_wallet?.substring(0, 15)}...
                          </div>
                        </div>
                        <div className="text-right">
                          <a 
                            href={`https://etherscan.io/tx/${w.tx_hash}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[#3b82f6] hover:underline text-sm"
                          >
                            View on Etherscan →
                          </a>
                          <div className="text-xs text-muted-foreground mt-1">
                            By: {w.withdrawn_by}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-muted-foreground">
                    <Download className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No withdrawals yet</p>
                    <p className="text-sm mt-2">Use the "Withdraw Earnings" button at the top to transfer fees</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Admin DeFi Testing Panel */}
      <Card className="glass border-border mb-6">
        <CardHeader>
          <CardTitle>Admin DeFi Testing & Execution</CardTitle>
          <CardDescription>Execute real DeFi transactions for testing or protocol management</CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="stake">
            <TabsList className="grid grid-cols-5 mb-6">
              <TabsTrigger value="stake">Stake</TabsTrigger>
              <TabsTrigger value="deposit">Deposit</TabsTrigger>
              <TabsTrigger value="borrow">Borrow</TabsTrigger>
              <TabsTrigger value="leverage">Leverage</TabsTrigger>
              <TabsTrigger value="view">View Position</TabsTrigger>
            </TabsList>

            {/* Stake BETH */}
            <TabsContent value="stake">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Amount to Stake (BETH)</label>
                  <Input type="number" placeholder="100" id="admin-stake-amount" />
                </div>
                <Button 
                  className="w-full bg-[#10b981] hover:bg-[#059669]"
                  onClick={async () => {
                    const amount = document.getElementById('admin-stake-amount').value;
                    if (!amount) {
                      toast.error('Please enter amount');
                      return;
                    }
                    try {
                      const response = await axios.post(
                        `${API}/defi/stake`,
                        { amount: parseFloat(amount) },
                        { headers: getAuthHeaders() }
                      );
                      toast.success(`Staked ${amount} BETH successfully!`);
                      console.log('Stake response:', response.data);
                    } catch (error) {
                      toast.error(error.response?.data?.detail || 'Stake failed');
                    }
                  }}
                >
                  Execute Stake Transaction
                </Button>
                <p className="text-xs text-muted-foreground">
                  Note: Uses admin wallet. Make sure you have BETH tokens and Sepolia ETH for gas.
                </p>
              </div>
            </TabsContent>

            {/* Deposit Collateral */}
            <TabsContent value="deposit">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Collateral Type</label>
                  <select className="w-full p-2 bg-secondary rounded border border-border" id="admin-collateral-type">
                    <option value="eth">ETH</option>
                    <option value="weth">WETH</option>
                    <option value="beth">BETH</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Amount</label>
                  <Input type="number" placeholder="0.1" step="0.001" id="admin-deposit-amount" />
                </div>
                <Button 
                  className="w-full bg-[#3b82f6] hover:bg-[#2563eb]"
                  onClick={async () => {
                    const type = document.getElementById('admin-collateral-type').value;
                    const amount = document.getElementById('admin-deposit-amount').value;
                    if (!amount) {
                      toast.error('Please enter amount');
                      return;
                    }
                    try {
                      const response = await axios.post(
                        `${API}/defi/deposit-collateral`,
                        { collateral_type: type, amount: parseFloat(amount) },
                        { headers: getAuthHeaders() }
                      );
                      toast.success(`Deposited ${amount} ${type.toUpperCase()} as collateral!`);
                      console.log('Deposit response:', response.data);
                    } catch (error) {
                      toast.error(error.response?.data?.detail || 'Deposit failed');
                    }
                  }}
                >
                  Execute Deposit Transaction
                </Button>
              </div>
            </TabsContent>

            {/* Borrow BUSD */}
            <TabsContent value="borrow">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Amount to Borrow (BUSD)</label>
                  <Input type="number" placeholder="100" id="admin-borrow-amount" />
                </div>
                <Button 
                  className="w-full bg-[#8b5cf6] hover:bg-[#7c3aed]"
                  onClick={async () => {
                    const amount = document.getElementById('admin-borrow-amount').value;
                    if (!amount) {
                      toast.error('Please enter amount');
                      return;
                    }
                    try {
                      const response = await axios.post(
                        `${API}/defi/borrow`,
                        { amount: parseFloat(amount) },
                        { headers: getAuthHeaders() }
                      );
                      toast.success(`Borrowed ${amount} BUSD successfully!`);
                      console.log('Borrow response:', response.data);
                    } catch (error) {
                      toast.error(error.response?.data?.detail || 'Borrow failed');
                    }
                  }}
                >
                  Execute Borrow Transaction
                </Button>
                <div className="text-xs text-muted-foreground p-3 bg-orange-500/10 border border-orange-500/50 rounded">
                  <p className="font-semibold">⚠️ Important:</p>
                  <p>You must deposit collateral first. Borrowing limited to 66% of collateral value.</p>
                </div>
              </div>
            </TabsContent>

            {/* Apply Leverage */}
            <TabsContent value="leverage">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Leverage Multiplier</label>
                  <select className="w-full p-2 bg-secondary rounded border border-border" id="admin-leverage">
                    <option value="1">1x (No leverage)</option>
                    <option value="2">2x (Pro tier)</option>
                    <option value="3">3x (Premium tier)</option>
                  </select>
                </div>
                <Button 
                  className="w-full bg-[#ef4444] hover:bg-[#dc2626]"
                  onClick={async () => {
                    const leverage = document.getElementById('admin-leverage').value;
                    try {
                      const response = await axios.post(
                        `${API}/defi/leverage`,
                        { leverage_multiplier: parseFloat(leverage) },
                        { headers: getAuthHeaders() }
                      );
                      toast.success(`Applied ${leverage}x leverage!`);
                      console.log('Leverage response:', response.data);
                    } catch (error) {
                      toast.error(error.response?.data?.detail || 'Leverage failed');
                    }
                  }}
                >
                  Execute Leverage Transaction
                </Button>
                <div className="text-xs text-muted-foreground p-3 bg-red-500/10 border border-red-500/50 rounded">
                  <p className="font-semibold">⚠️ High Risk:</p>
                  <p>Leverage amplifies both gains and losses. Use with caution in production.</p>
                </div>
              </div>
            </TabsContent>

            {/* View Position */}
            <TabsContent value="view">
              <div className="space-y-4">
                <Button 
                  className="w-full"
                  onClick={async () => {
                    try {
                      const [position, staking, balance] = await Promise.all([
                        axios.get(`${API}/defi/position`, { headers: getAuthHeaders() }),
                        axios.get(`${API}/defi/staking-info`, { headers: getAuthHeaders() }),
                        axios.get(`${API}/defi/beth-balance`, { headers: getAuthHeaders() })
                      ]);
                      
                      console.log('Position:', position.data);
                      console.log('Staking:', staking.data);
                      console.log('Balance:', balance.data);
                      
                      toast.success('Position data loaded - check console (F12)');
                      
                      // Display in UI
                      const display = `
Position:
- Collateral: $${position.data.total_collateral_usd?.toFixed(2) || '0.00'}
- Debt: ${position.data.debt_amount || 0} BUSD
- Health Factor: ${position.data.health_factor || '∞'}

Staking:
- Staked: ${staking.data.staked || 0} BETH
- Rewards: ${staking.data.rewards || 0} BETH

Balance:
- BETH: ${balance.data.balance || 0}
                      `;
                      
                      alert(display);
                    } catch (error) {
                      toast.error('Failed to load position data');
                      console.error(error);
                    }
                  }}
                >
                  Load Admin Position Data
                </Button>
                <p className="text-sm text-muted-foreground">
                  View your current DeFi position, staking info, and BETH balance. Data will appear in browser console and alert.
                </p>
              </div>
            </TabsContent>
          </Tabs>

          <div className="mt-6 p-4 bg-blue-500/10 border border-blue-500/50 rounded">
            <p className="text-sm font-semibold mb-2">💡 How Admin DeFi Works:</p>
            <ul className="text-xs space-y-1">
              <li>• Transactions execute with your admin wallet (connected to app)</li>
              <li>• Real blockchain transactions on Sepolia testnet</li>
              <li>• Need Sepolia ETH for gas fees</li>
              <li>• Use for testing protocol or managing liquidity</li>
              <li>• All transactions are logged in console</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      {/* Token Whitelist Management */}
      <Card className="glass border-border">
        <CardHeader>
          <CardTitle>Token Whitelist Management</CardTitle>
          <CardDescription>Approve tokens for use as collateral in DeFi protocol</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {/* Whitelist Form */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-4 bg-secondary rounded-lg">
              <div>
                <label className="block text-sm font-medium mb-2">Contract Address</label>
                <Input placeholder="0x..." className="font-mono text-xs" id="token-address" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Token Name</label>
                <Input placeholder="e.g., My Token" id="token-name" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Symbol</label>
                <Input placeholder="e.g., MTK" id="token-symbol" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Price (USD)</label>
                <Input type="number" step="0.01" placeholder="1.00" id="token-price" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Collateral Ratio</label>
                <Input type="number" step="0.01" defaultValue="0.66" placeholder="0.66" id="token-ratio" />
              </div>
              <div className="flex items-end">
                <Button 
                  className="w-full bg-[#10b981] hover:bg-[#059669]"
                  onClick={async () => {
                    const address = document.getElementById('token-address').value;
                    const name = document.getElementById('token-name').value;
                    const symbol = document.getElementById('token-symbol').value;
                    const price = parseFloat(document.getElementById('token-price').value);
                    const ratio = parseFloat(document.getElementById('token-ratio').value);
                    
                    if (!address || !name || !symbol || !price) {
                      toast.error('Please fill all fields');
                      return;
                    }
                    
                    try {
                      await axios.post(
                        `${API}/admin/whitelist-token`,
                        null,
                        {
                          params: { contract_address: address, token_name: name, token_symbol: symbol, price_usd: price, collateral_ratio: ratio },
                          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                        }
                      );
                      toast.success('Token whitelisted successfully!');
                      // Clear form
                      document.getElementById('token-address').value = '';
                      document.getElementById('token-name').value = '';
                      document.getElementById('token-symbol').value = '';
                      document.getElementById('token-price').value = '';
                    } catch (error) {
                      toast.error(error.response?.data?.detail || 'Failed to whitelist token');
                    }
                  }}
                >
                  Whitelist Token
                </Button>
              </div>
            </div>

            <div className="text-sm text-muted-foreground p-3 bg-blue-500/10 border border-blue-500/50 rounded">
              <p className="font-semibold mb-1">ℹ️ How Token Collateral Works:</p>
              <ul className="space-y-1 text-xs">
                <li>• Users can deposit whitelisted tokens as collateral</li>
                <li>• Set price manually (update as needed)</li>
                <li>• Collateral ratio determines max borrow (66% = borrow up to 66% of value)</li>
                <li>• Users get instant liquidity without selling tokens</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Admin;
